import { Tab } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { getDynamicRoute, useAppParams } from 'lib/frontend/utils/router';
import React, { FC, useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Badge } from '../Badge';
import { ColumnButton } from '../ColumnButton';

export type RouterTabOption = {
  /** Whether the tab is clickable. */
  enabled: boolean;
  /** The label to display in the tab. */
  label: React.ReactNode;
  /** The path to navigate to when the tab is clicked. */
  path: string;
  /** Optional hotkey to select the tab via keyboard. */
  hotKey?: string;
  /** Optional icon to display alongside the tab label. */
  Icon?: FC<{ className?: string }>;
};

function RouterTab({
  path,
  enabled,
  hotKey,
  Icon,
  label,
  sidebar,
  preserveSearchParams = false
}: RouterTabOption & {
  sidebar?: boolean;
  preserveSearchParams?: boolean;
}) {
  const location = useLocation();
  const isActive = location.pathname.startsWith(path);

  const navigate = useNavigate();
  const onSelect = () => {
    if (!enabled) return;

    // Preserve search parameters when navigating if preserveSearchParams is true
    if (preserveSearchParams) {
      const { search } = location;
      navigate(`${path}${search}`);
    } else {
      navigate(path);
    }
  };

  useHotkeys(hotKey || '', onSelect);

  if (sidebar) {
    return (
      <button
        className={classNames(
          'flex rounded font-medium text-base leading-5 mt-1 py-3 w-56',
          {
            'cursor-pointer hover:bg-column-primary-50 hover:text-primary-500 text-column-gray-500':
              !isActive && enabled,
            'bg-column-primary-50 text-primary-500': isActive,
            'cursor-not-allowed text-column-gray-300': !enabled
          }
        )}
        disabled={!enabled}
        role="tab"
        onClick={onSelect}
      >
        {Icon && (
          <div className="flex justify-center w-1/6">
            <Icon className="w-5 h-5" />
          </div>
        )}
        <span>{label}</span>
        {hotKey && <Badge>{hotKey.toUpperCase()}</Badge>}
      </button>
    );
  }

  return (
    <Tab
      className={classNames('ml-6 focus:outline-none', {
        'cursor-not-allowed': !enabled
      })}
      onClick={onSelect}
    >
      <div
        aria-selected={isActive}
        className={classNames(
          'text-base font-medium py-6 flex items-center gap-2',
          {
            'text-column-primary-600 border-b-2 border-column-primary-500':
              isActive
          },
          {
            'text-column-gray-500': !isActive && enabled
          },
          {
            'text-column-gray-300': !enabled
          }
        )}
        role="tab"
      >
        {Icon && <Icon className="w-5" />}
        <span>{label}</span>
        {hotKey && <Badge>{hotKey.toUpperCase()}</Badge>}
      </div>
    </Tab>
  );
}

function SidebarWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-screen flex justify-center gap-6 p-8">
      <div className="mt-3">{children}</div>
      <div className="flex-1 max-w-6xl">
        <Outlet />
      </div>
    </div>
  );
}

function HorizontalWrapper({
  children,
  id
}: {
  children: React.ReactNode;
  id: string;
}) {
  return (
    <Tab.List
      className={classNames('w-full border-b border-column-gray-100')}
      id={id}
    >
      {children}
    </Tab.List>
  );
}

function HorizontalOverflowWrapper({
  children,
  id
}: {
  children: React.ReactNode;
  id: string;
}) {
  const location = useLocation();

  useEffect(() => {
    const container = document.getElementById(`${id}-tab-container`);

    if (!container) {
      return;
    }

    const activeTab = container.querySelector<HTMLDivElement>(
      '[aria-selected="true"] div'
    );

    if (!activeTab) {
      return;
    }

    container.scrollTo({
      left: activeTab.offsetLeft - 78,
      behavior: 'smooth'
    });
  }, [location.pathname]);

  return (
    <div id={`${id}-tab-container`} className="overflow-auto hide-scrollbar">
      <Tab.List
        className="relative w-max min-w-full border-b border-column-gray-100 flex"
        id={id}
      >
        <div className="sticky left-0 bg-white rounded p-4">
          <ColumnButton
            type="button"
            startIcon={<ChevronLeftIcon className="w-5 h-5" />}
            onClick={() => {
              const container = document.getElementById(`${id}-tab-container`);
              if (container) {
                container.scrollLeft -= container.offsetWidth;
              }
            }}
          />
        </div>
        <div className="-ml-6" />
        {children}
        <div className="sticky right-0 bg-white rounded p-4">
          <ColumnButton
            type="button"
            startIcon={<ChevronRightIcon className="w-5 h-5" />}
            onClick={() => {
              const container = document.getElementById(`${id}-tab-container`);
              if (container) {
                container.scrollLeft += container.offsetWidth;
              }
            }}
          />
        </div>
      </Tab.List>
    </div>
  );
}

type RouterTabsProps = {
  /**
   * List of tab options to render. Each should be separately handled by routing
   * logic to properly render the tab content.
   */
  tabs: RouterTabOption[];
  /** Unique identifier for the tab group. */
  id: string;
  /**
   * Optional flag to render the tabs in a sidebar. (By default, tabs are
   * rendered in a horizontal tab group.)
   */
  sidebar?: boolean;
  /**
   * Optional flag to hide disabled tabs. (By default, disabled tabs are
   * visible, but not clickable.)
   */
  hideDisabled?: boolean;
  /**
   * Optional flag to enable overflow buttons and scrolling
   */
  overflow?: boolean;
};

/**
 * Generalized tab component that integrates with react-router to render tabs
 * based on the current route.
 */
export default function RouterTabs({
  tabs,
  id,
  sidebar,
  hideDisabled,
  preserveSearchParams = false,
  overflow = false
}: RouterTabsProps & { preserveSearchParams?: boolean }) {
  const location = useLocation();
  const params = useAppParams(location.pathname);
  const relevantTabs = hideDisabled ? tabs.filter(tab => tab.enabled) : tabs;

  const defaultIndex = relevantTabs.findIndex(tab => tab.enabled);

  const currentTab = relevantTabs.find(
    tab =>
      tab.enabled &&
      location.pathname.startsWith(getDynamicRoute(tab.path, params))
  );

  /**
   * If current route does not match an enabled tab path, navigate to the first
   * enabled tab
   */
  if (!currentTab) {
    return (
      <Navigate
        to={getDynamicRoute(relevantTabs[defaultIndex].path, params)}
        replace
      />
    );
  }

  if (sidebar) {
    return (
      <SidebarWrapper>
        {relevantTabs.map(tab => (
          <RouterTab
            key={tab.path}
            {...tab}
            sidebar
            preserveSearchParams={preserveSearchParams}
          />
        ))}
      </SidebarWrapper>
    );
  }

  const tabComponents = relevantTabs.map(tab => (
    <RouterTab
      key={tab.path}
      {...tab}
      preserveSearchParams={preserveSearchParams}
    />
  ));

  return (
    <Tab.Group defaultIndex={defaultIndex}>
      {!overflow && (
        <HorizontalWrapper id={id}>{tabComponents}</HorizontalWrapper>
      )}
      {overflow && (
        <HorizontalOverflowWrapper id={id}>
          {tabComponents}
        </HorizontalOverflowWrapper>
      )}
      <Outlet />
    </Tab.Group>
  );
}
